import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import AddIcon from '@mui/icons-material/Add';
import MainContent from '../components/MainContent';
import Modal from '../components/Modal';
import { canSendImpactReport } from '../utils/permissionKeys';
import {
  fetchPartnerLogo,
  partnerError,
  useReportPartners,
  useSavePartner,
} from '../hooks/useReportPartners';
import {
  MAX_RECIPIENTS,
  parseRecipients,
} from '../components/impact/pdf/email';

const CARD = 'bg-white rounded-xl border border-gray-200 shadow-sm';
const MAX_LOGO_BYTES = 1024 * 1024;
const COLOUR_RE = /^#[0-9a-fA-F]{6}$/;
const URL_RE = /^https?:\/\/\S+\.\S+$/;

export function PartnerLogo({ partner, className = 'h-12' }) {
  const { data: url } = useQuery(
    ['report-partner-logo', partner.id, partner.logo_version],
    () => fetchPartnerLogo(partner),
    { enabled: Boolean(partner.has_logo), staleTime: Infinity, retry: false }
  );
  if (!partner.has_logo || !url) {
    return (
      <div
        className={`${className} aspect-[3/2] rounded-md bg-gray-100 flex items-center justify-center text-[10px] text-gray-400`}
      >
        No logo
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={`${partner.name} logo`}
      className={`${className} w-auto max-w-[10rem] object-contain`}
    />
  );
}

function PartnerForm({ partner, onClose }) {
  const save = useSavePartner();
  const [name, setName] = useState(partner?.name ?? '');
  const [website, setWebsite] = useState(partner?.website ?? '');
  const [colour, setColour] = useState(partner?.brand_colour ?? '');
  const [recipientsText, setRecipientsText] = useState(
    (partner?.default_recipients ?? []).join('\n')
  );
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [logoProblem, setLogoProblem] = useState(null);

  useEffect(
    () => () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    },
    [logoPreview]
  );

  const { valid: recipients, invalid } = parseRecipients(recipientsText);
  let problem = null;
  if (!name.trim()) problem = 'Add the partner name.';
  else if (website.trim() && !URL_RE.test(website.trim())) {
    problem = 'The website should start with http:// or https://';
  } else if (colour && !COLOUR_RE.test(colour)) {
    problem = 'The brand colour should look like #8B173B.';
  } else if (invalid.length) {
    problem = `Check these addresses: ${invalid.join(', ')}`;
  } else if (recipients.length > MAX_RECIPIENTS) {
    problem = `At most ${MAX_RECIPIENTS} default recipients.`;
  } else if (logoProblem) problem = logoProblem;

  const pickLogo = (file) => {
    setLogo(null);
    setLogoProblem(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setLogoProblem('Use a PNG or JPEG logo (not SVG).');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoProblem('The logo must be 1 MB or smaller.');
      return;
    }
    setLogo(file);
    setRemoveLogo(false);
    setLogoPreview(URL.createObjectURL(file));
  };

  const submit = () =>
    save.mutate(
      {
        id: partner?.id,
        values: {
          name: name.trim(),
          website: website.trim(),
          brand_colour: colour,
          default_recipients: recipients,
          ...(logo ? { logo } : {}),
          ...(removeLogo ? { remove_logo: true } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success(partner ? 'Partner updated.' : 'Partner added.');
          onClose();
        },
      }
    );

  return (
    <div className="space-y-4 text-sm">
      <label className="block space-y-1">
        <span className="text-xs font-medium text-gray-700">Name</span>
        <input
          value={name}
          maxLength={120}
          onChange={(event) => setName(event.target.value)}
          placeholder="KPC Foundation"
          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-claret-500"
        />
      </label>

      <div className="space-y-1">
        <span className="text-xs font-medium text-gray-700">Logo</span>
        <div className="flex items-center gap-4">
          {logoPreview ? (
            <img
              src={logoPreview}
              alt="New logo preview"
              className="h-14 w-auto max-w-[10rem] object-contain"
            />
          ) : (
            partner &&
            !removeLogo && <PartnerLogo partner={partner} className="h-14" />
          )}
          <div className="space-y-1">
            <input
              type="file"
              accept="image/png,image/jpeg"
              aria-label="Logo file"
              onChange={(event) => pickLogo(event.target.files?.[0])}
              className="text-xs"
            />
            <p className="text-[11px] text-gray-500">
              PNG or JPEG, up to 1 MB. A wide logo on a transparent or white
              background prints best.
            </p>
            {partner?.has_logo && !logo && (
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={removeLogo}
                  onChange={(event) => setRemoveLogo(event.target.checked)}
                />
                Remove the current logo
              </label>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-gray-700">
            Website (optional)
          </span>
          <input
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
            placeholder="https://partner.org"
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-claret-500"
          />
        </label>
        <div className="space-y-1">
          <span className="text-xs font-medium text-gray-700">
            Brand colour (optional)
          </span>
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label="Brand colour picker"
              value={COLOUR_RE.test(colour) ? colour : '#8B173B'}
              onChange={(event) => setColour(event.target.value.toUpperCase())}
              className="h-9 w-12 rounded border border-gray-300"
            />
            <input
              value={colour}
              onChange={(event) => setColour(event.target.value.trim())}
              placeholder="#8B173B"
              aria-label="Brand colour"
              className="w-28 rounded-md border border-gray-300 px-3 py-2 font-mono text-xs"
            />
            {colour && (
              <button
                type="button"
                onClick={() => setColour('')}
                className="text-xs text-gray-500 hover:underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium text-gray-700">
          Default recipients (optional)
        </span>
        <textarea
          rows={3}
          value={recipientsText}
          onChange={(event) => setRecipientsText(event.target.value)}
          placeholder="contact@partner.org"
          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-claret-500"
        />
        <span className="block text-[11px] text-gray-500">
          Pre-filled when emailing this partner&apos;s report. One per line or
          comma-separated.
        </span>
      </label>

      {(problem || save.isError) && (
        <p className="text-xs text-red-700">
          {save.isError
            ? partnerError(save.error, "We couldn't save the partner.")
            : name && problem}
        </p>
      )}

      <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-gray-300 px-4 py-2 text-gray-700"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={Boolean(problem) || save.isLoading}
          onClick={submit}
          className="rounded-md bg-claret-500 px-4 py-2 text-white hover:bg-claret-400 disabled:opacity-40"
        >
          {save.isLoading ? 'Saving…' : 'Save partner'}
        </button>
      </div>
    </div>
  );
}

function ReportPartners() {
  const user = useSelector((state) => state.auth.userDetails) ?? {};
  const permissions = useSelector((state) => state.auth.permissions ?? []);
  const canManage = canSendImpactReport(permissions, user.role);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | partner
  const {
    data: partners = [],
    isLoading,
    error,
  } = useReportPartners(showArchived ? 'all' : 'active');
  const archive = useSavePartner();

  const setStatus = (partner, status) =>
    archive.mutate(
      { id: partner.id, values: { status } },
      {
        onSuccess: () =>
          toast.success(
            status === 'archived'
              ? `${partner.name} archived.`
              : `${partner.name} restored.`
          ),
        onError: (err) =>
          toast.error(partnerError(err, "We couldn't update the partner.")),
      }
    );

  return (
    <MainContent full>
      <div className="space-y-5 pb-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <nav className="text-xs text-gray-500 mb-1">
              <Link to="/impact" className="hover:text-claret-500">
                Impact Report
              </Link>{' '}
              › <span className="text-gray-900">Report partners</span>
            </nav>
            <h1 className="text-2xl font-semibold text-gray-900">
              Report partners
            </h1>
            <p className="text-xs text-gray-500">
              Names and logos used to co-brand impact reports. Pick one in
              Download → Branded PDF report.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(event) => setShowArchived(event.target.checked)}
              />
              Show archived
            </label>
            {canManage && (
              <button
                type="button"
                onClick={() => setEditing('new')}
                className="inline-flex items-center gap-1 rounded-md bg-claret-500 px-4 py-2 text-sm text-white hover:bg-claret-400"
              >
                <AddIcon fontSize="small" />
                Add partner
              </button>
            )}
          </div>
        </header>

        {!canManage && (
          <p className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
            You can use these partners in reports. Adding or editing them needs
            the SendImpactReport permission.
          </p>
        )}

        {(() => {
          if (isLoading) {
            return (
              <div
                className={`${CARD} h-40 animate-pulse`}
                aria-label="Loading"
              />
            );
          }
          if (error) {
            return (
              <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
                {partnerError(error, "We couldn't load partners.")}
              </div>
            );
          }
          if (!partners.length) {
            return (
              <div className={`${CARD} p-10 text-center space-y-1`}>
                <p className="text-sm text-gray-900">No partners yet</p>
                <p className="text-xs text-gray-500">
                  Add a partner&apos;s name and logo to co-brand their reports.
                </p>
              </div>
            );
          }
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {partners.map((partner) => (
                <article
                  key={partner.id}
                  className={`${CARD} p-5 space-y-3 ${
                    partner.status === 'archived' ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <PartnerLogo partner={partner} />
                    {partner.brand_colour && (
                      <span
                        className="h-5 w-5 rounded-full border border-gray-200"
                        style={{ backgroundColor: partner.brand_colour }}
                        title={`Brand colour ${partner.brand_colour}`}
                      />
                    )}
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      {partner.name}
                      {partner.status === 'archived' && (
                        <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-normal text-gray-600">
                          Archived
                        </span>
                      )}
                    </h2>
                    {partner.website && (
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-claret-500 hover:underline break-all"
                      >
                        {partner.website}
                      </a>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {(partner.default_recipients ?? []).length
                        ? `Default recipients: ${partner.default_recipients.join(
                            ', '
                          )}`
                        : 'No default recipients'}
                    </p>
                  </div>
                  {canManage && (
                    <div className="flex gap-3 border-t border-gray-100 pt-3 text-xs">
                      <button
                        type="button"
                        onClick={() => setEditing(partner)}
                        className="text-claret-500 hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setStatus(
                            partner,
                            partner.status === 'archived'
                              ? 'active'
                              : 'archived'
                          )
                        }
                        className="text-gray-500 hover:underline"
                      >
                        {partner.status === 'archived' ? 'Restore' : 'Archive'}
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          );
        })()}
      </div>

      <Modal
        modalOpen={Boolean(editing)}
        title={editing === 'new' ? 'Add partner' : 'Edit partner'}
        onClose={() => setEditing(null)}
      >
        {editing && (
          <PartnerForm
            key={editing === 'new' ? 'new' : editing.id}
            partner={editing === 'new' ? null : editing}
            onClose={() => setEditing(null)}
          />
        )}
      </Modal>
    </MainContent>
  );
}

export default ReportPartners;
